import type {
  BreakerSelectivityResponse,
  BreakerSelectivityWarning,
} from "../services/breakerSelectivity";
import { StatusIcon } from "./StatusIcon";
import { GROUP_TONE } from "./statusTone";

type WarningCode = BreakerSelectivityWarning["code"];

type WarningGroup =
  | "not-met"
  | "selectivity-not-met"
  | "review-evidence"
  | "fault-level"
  | "not-evaluated"
  | "other"
  | "declared-evidence";

interface BreakerSelectivityWarningPanelProps {
  result: BreakerSelectivityResponse;
}

const NO_WARNINGS_TEXT = "No engineering warnings raised.";

// Exhaustive maps: a new code without a label or a group is a compile error. A
// breaker selectivity warning carries only a code and a message - there is no
// severity and no reference field on this contract, so none is shown.
const WARNING_CODE_LABELS: Record<WarningCode, string> = {
  ICU_BELOW_PROSPECTIVE_CURRENT: "Icu below the prospective current",
  ICS_BELOW_PROSPECTIVE_CURRENT: "Ics below the prospective current",
  ICM_BELOW_PEAK_CURRENT: "Icm below the peak current",
  RELIES_ON_CASCADING: "Relies on declared cascading",
  MAKING_CHECK_NOT_EVALUATED: "Making check not evaluated",
  RATED_VOLTAGE_BELOW_SYSTEM: "Rated operational voltage below the system voltage",
  SELECTIVITY_EVIDENCE_NOT_ESTABLISHED: "Selectivity evidence not established",
  SELECTIVITY_NOT_MET: "Selectivity not met at the prospective current",
  SELECTIVITY_PARTIAL_NOT_REQUIRED: "Partial selectivity ends below Ik'' (not required)",
  PROSPECTIVE_CURRENT_NOT_LINKED: "Prospective current not linked to a fault run",
  FAULT_RUN_NOT_MAXIMUM_THREE_PHASE: "Fault run not a maximum three-phase study",
  FAULT_RUN_BUS_MISMATCH: "Fault run bus differs from the study bus",
  BREAKER_EVIDENCE_MANUFACTURER_DECLARED: "Breaker evidence manufacturer-declared",
};

// Where each code is listed. A device that failed a duty check comes first (there is
// nothing to accept), then a required selectivity that is not met, then the evidence
// the engineer still owes - declared cascading (never a pass), a rated voltage below
// the system voltage (IR-001 F1), or no selectivity evidence - then the fault level
// that is not the linked maximum three-phase study of the bus, then the making check
// that had no peak to be made against, then the plain warnings, and last the
// declared-evidence limit that every result carries (A21 (g), GAP-021).
const WARNING_CODE_GROUPS: Record<WarningCode, WarningGroup> = {
  ICU_BELOW_PROSPECTIVE_CURRENT: "not-met",
  ICS_BELOW_PROSPECTIVE_CURRENT: "not-met",
  ICM_BELOW_PEAK_CURRENT: "not-met",
  SELECTIVITY_NOT_MET: "selectivity-not-met",
  RELIES_ON_CASCADING: "review-evidence",
  RATED_VOLTAGE_BELOW_SYSTEM: "review-evidence",
  SELECTIVITY_EVIDENCE_NOT_ESTABLISHED: "review-evidence",
  PROSPECTIVE_CURRENT_NOT_LINKED: "fault-level",
  FAULT_RUN_NOT_MAXIMUM_THREE_PHASE: "fault-level",
  FAULT_RUN_BUS_MISMATCH: "fault-level",
  MAKING_CHECK_NOT_EVALUATED: "not-evaluated",
  SELECTIVITY_PARTIAL_NOT_REQUIRED: "other",
  BREAKER_EVIDENCE_MANUFACTURER_DECLARED: "declared-evidence",
};

const GROUP_ORDER: readonly { group: WarningGroup; title: string }[] = [
  { group: "not-met", title: "Duty not met" },
  { group: "selectivity-not-met", title: "Selectivity not met" },
  { group: "review-evidence", title: "Review required (evidence)" },
  { group: "fault-level", title: "Fault level (review required)" },
  { group: "not-evaluated", title: "Not evaluated" },
  { group: "other", title: "Other warnings" },
  { group: "declared-evidence", title: "Declared evidence" },
];

// One listed warning: a device's warning names its device, a study warning stands alone.
type ListedWarning = {
  key: string;
  code: WarningCode;
  message: string;
  prefix: string;
};

/** The study's warnings, then the incomer's, then every outgoing device's, in the engine's order. */
export function listWarnings(result: BreakerSelectivityResponse): ListedWarning[] {
  const study = result.warnings.map((warning, index) => ({
    key: `study-${warning.code}-${index}`,
    code: warning.code,
    message: warning.message,
    prefix: "",
  }));
  const devices = [result.incomer, ...result.outgoing].flatMap((device) =>
    device.warnings.map((warning, index) => ({
      key: `${device.code}-${warning.code}-${index}`,
      code: warning.code,
      message: warning.message,
      prefix: `${device.code}: `,
    })),
  );
  return [...study, ...devices];
}

export function BreakerSelectivityWarningPanel({ result }: BreakerSelectivityWarningPanelProps) {
  const warnings = listWarnings(result);

  return (
    <section aria-label="Engineering warnings">
      <h3>Engineering warnings</h3>
      {warnings.length === 0 ? (
        <p data-no-warnings="true">{NO_WARNINGS_TEXT}</p>
      ) : (
        <div data-warning-count={warnings.length}>
          {GROUP_ORDER.map(({ group, title }) => {
            // Within a group the study's order, then each device's own order, is kept.
            const members = warnings.filter(
              (warning) => WARNING_CODE_GROUPS[warning.code] === group,
            );
            if (members.length === 0) {
              return null;
            }
            return (
              <section key={group} aria-label={title} data-warning-group={group}>
                <h4>
                  <StatusIcon tone={GROUP_TONE[group]} size={20} />
                  {title}
                </h4>
                <ul>
                  {members.map((warning) => (
                    <li key={warning.key} data-warning-code={warning.code}>
                      <strong>
                        {warning.prefix}
                        {WARNING_CODE_LABELS[warning.code]}
                      </strong>
                      {": "}
                      {/* The engine's own sentence, kept as it was sent. */}
                      <span>{warning.message}</span>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </section>
  );
}

export { GROUP_ORDER, WARNING_CODE_GROUPS, WARNING_CODE_LABELS };

export default BreakerSelectivityWarningPanel;
